/* P3 — Consent gate.
 *
 * Renders a full-screen blocking modal when the user hasn't accepted the
 * current Terms version OR hasn't attested that they inform patients about
 * AI usage. Both must be checked to proceed. Refusing = logout.
 *
 * Also renders a slim bottom cookie banner when the analytics consent has
 * never been answered.
 *
 * Never blocks the login/register/verify routes — only kicks in after auth.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Cookie, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { useConsentStatuses, CONSENT_TYPES } from '../hooks/useConsent';

const PUBLIC_PATHS = new Set([
  '/', '/login', '/register', '/forgot-password', '/reset-password',
  '/verify-email', '/admin-akali-2026', '/admin-invite',
]);

const isPublicPath = (pathname) => {
  if (PUBLIC_PATHS.has(pathname)) return true;
  return false;
};

export const ConsentGate = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const active = Boolean(user) && !user.is_admin && !isPublicPath(location.pathname);

  const { statuses, refresh, recordConsent } = useConsentStatuses({ enabled: active });

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptAiNotice, setAcceptAiNotice] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const termsStatus = statuses?.[CONSENT_TYPES.TERMS_ACCEPTED];
  const aiNoticeStatus = statuses?.[CONSENT_TYPES.AI_USAGE_PATIENT_NOTICE];
  const cookiesStatus = statuses?.[CONSENT_TYPES.COOKIES_ANALYTICS];

  const needsGate = useMemo(() => {
    if (!active || !statuses) return false;
    return Boolean(termsStatus?.needsAttestation || aiNoticeStatus?.needsAttestation);
  }, [active, statuses, termsStatus, aiNoticeStatus]);

  // Reset the checkbox state whenever the modal re-opens so a stale click
  // from a previous session never accidentally submits.
  useEffect(() => {
    if (needsGate) {
      setAcceptTerms(false);
      setAcceptAiNotice(false);
    }
  }, [needsGate]);

  const handleAccept = async () => {
    if (!acceptTerms || !acceptAiNotice || submitting) return;
    setSubmitting(true);
    try {
      // Fire both consents. Backend is append-only; ordering doesn't matter.
      await Promise.all([
        recordConsent({
          type: CONSENT_TYPES.TERMS_ACCEPTED,
          granted: true,
          contentVersion: termsStatus?.currentVersion || 'v1',
        }),
        recordConsent({
          type: CONSENT_TYPES.AI_USAGE_PATIENT_NOTICE,
          granted: true,
          contentVersion: aiNoticeStatus?.currentVersion || 'v1',
        }),
      ]);
      // refresh is called inside recordConsent, but ensure freshness anyway.
      await refresh();
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefuse = async () => {
    // User rejected the terms — sign them out (LGPD: no access without consent).
    try { await logout(); } catch { /* ignore */ }
    navigate('/login', { replace: true });
  };

  const handleAnalyticsChoice = async (granted) => {
    await recordConsent({
      type: CONSENT_TYPES.COOKIES_ANALYTICS,
      granted,
      contentVersion: 'v1',
    });
  };

  return (
    <>
      {children}

      {needsGate && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          data-testid="terms-gate-modal"
        >
          <div className="max-w-lg w-full bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-full bg-amber-100 dark:bg-amber-900/40 p-2">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Termos e uso de IA
                </h3>
                <p className="text-sm mt-1 text-slate-600 dark:text-slate-400">
                  Antes de continuar, precisamos do seu aceite. Ambas as caixas são obrigatórias.
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={acceptTerms}
                  onCheckedChange={(v) => setAcceptTerms(Boolean(v))}
                  data-testid="terms-gate-accept-terms"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Aceito os <a
                    href="/terms"
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-primary"
                    data-testid="terms-gate-terms-link"
                  >Termos de Uso vigentes</a> (versão {termsStatus?.currentVersion || 'v1'}).
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={acceptAiNotice}
                  onCheckedChange={(v) => setAcceptAiNotice(Boolean(v))}
                  data-testid="terms-gate-accept-ai-notice"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  Comprometo-me a informar meus pacientes de que a documentação clínica
                  é auxiliada por inteligência artificial e a obter, quando aplicável, o
                  consentimento presencial do paciente (versão {aiNoticeStatus?.currentVersion || 'v1'}).
                </span>
              </label>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleRefuse}
                data-testid="terms-gate-refuse-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Recusar e sair
              </Button>
              <Button
                onClick={handleAccept}
                disabled={!acceptTerms || !acceptAiNotice || submitting}
                data-testid="terms-gate-accept-btn"
                className="bg-primary hover:bg-primary-hover text-white"
              >
                {submitting ? 'Registrando…' : 'Aceito e continuar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {active && !needsGate && cookiesStatus?.needsAttestation && !cookiesStatus?.grantedAt && (
        <div
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-40 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg p-4 flex items-start gap-3"
          data-testid="cookie-banner"
        >
          <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-slate-700 dark:text-slate-300">
              Usamos cookies analíticos para entender o uso do produto e melhorar a experiência clínica.
              Você pode aceitar ou recusar — o app funciona igualmente nos dois casos.
            </p>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleAnalyticsChoice(false)}
                data-testid="cookie-banner-refuse-btn"
              >
                Recusar
              </Button>
              <Button
                size="sm"
                onClick={() => handleAnalyticsChoice(true)}
                className="bg-primary hover:bg-primary-hover text-white"
                data-testid="cookie-banner-accept-btn"
              >
                Aceitar
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
